import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import { ColumnDef } from '@tanstack/react-table'
import SelectField from 'app/frontend/base/components/form-fields/SelectField'
import {
  useNotificationTemplateBlacklists,
  useNotificationTemplateOptions,
  usePageInfo,
} from 'app/frontend/base/config/queries'
import { Trash2, Undo2 } from 'lucide-react'
import { useMemo } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

const name = 'notification_template_blacklists_attributes'

export type EditableBlacklist = wowaudit.NotificationTemplateBlacklist & {
  _destroy?: boolean
}

const NotificationBlacklistTable = () => {
  const { t } = useTranslation()
  const {
    data: { user },
  } = usePageInfo()

  const { data } = useNotificationTemplateBlacklists()
  const { teams, types } = data ?? {
    teams: [] as wowaudit.Team[],
    types: [] as string[],
  }

  const { data: templateOptions } = useNotificationTemplateOptions()

  const availableTypes = useMemo(() => {
    return templateOptions
      ? templateOptions
          .filter((option) => types.includes(option.className))
          .map((option) => ({
            label: t(`notifications.types.${option.key}.label`),
            value: option.className,
            category: option.category,
          }))
      : []
  }, [templateOptions, types])

  const { watch, setValue } = useFormContext()
  const { append, remove } = useFieldArray({ name })

  const blacklists: EditableBlacklist[] = watch(name)

  const columns: ColumnDef<EditableBlacklist>[] = useMemo(
    () => [
      {
        id: 'team_id',
        header: 'Blacklisted team',
        cell: (info) => {
          return (
            <SelectField<wowaudit.Team>
              layout='stacked'
              name={`${name}.${info.row.index}.team_id`}
              options={teams}
              disabled={!!info.row.original._destroy}
              className={info.row.original._destroy ? 'opacity-30' : ''}
              customPlaceholder={'All teams'}
              emptyMessage='You can only blacklist teams that have sent notifications to you before.'
              isClearable={true}
              optionLabelFn={(option) =>
                `${option.ownerReference.name} - ${option.name}`
              }
              customLabel={''}
              optionValueKey='id'
            />
          )
        },
      },
      {
        id: 'notification_template_id',
        header: 'Blacklisted notifications',
        size: 330,
        cell: (info) => {
          return (
            <SelectField
              layout='stacked'
              name={`${name}.${info.row.index}.notification_template_type`}
              customPlaceholder={'All notifications'}
              disabled={!!info.row.original._destroy}
              className={info.row.original._destroy ? 'opacity-30' : ''}
              isClearable={true}
              emptyMessage='You can only blacklist specific notifications if they have been sent to you before.'
              options={availableTypes}
              transformOptions={false}
              customLabel={''}
            />
          )
        },
      },
      {
        id: 'actions',
        header: '',
        size: 50,
        cell: (info) => {
          return (
            <div className='flex justify-end mt-0.5'>
              <Button
                type='button'
                size='sm'
                variant={
                  info.row.original._destroy ? 'secondary' : 'destructive'
                }
                onClick={() =>
                  info.row.original.id
                    ? setValue(
                        `${name}.${info.row.index}._destroy`,
                        !info.row.original._destroy,
                      )
                    : remove(info.row.index)
                }
              >
                {info.row.original._destroy ? (
                  <Undo2 size={14} />
                ) : (
                  <Trash2 size={14} />
                )}
              </Button>
            </div>
          )
        },
      },
    ],
    [teams, availableTypes],
  )

  return (
    <div>
      <DataTable
        className='mt-6'
        cellClassName='align-top px-2'
        includeHeader={blacklists && blacklists.length > 0}
        columns={columns}
        data={blacklists ?? []}
        emptyMessage={() => t('notifications.blacklist-empty-message')}
      />

      <div className='flex justify-end'>
        <Button
          type='button'
          size='sm'
          className='mt-2'
          variant='outline'
          onClick={() => append({ user_id: user?.id })}
        >
          {t('notifications.add-blacklist')}
        </Button>
      </div>
    </div>
  )
}

export default NotificationBlacklistTable
